import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell, ScatterChart, Scatter, ReferenceLine } from 'recharts';

// EPS-Based Removal Predictor
// Based on: Bai, Samari-Kermani et al. (2024, 2025) - SSF removal regression models
// Thesis Chapters 4, 5, 6

const EPSRemovalPredictor = () => {
  // ============== STATE ==============
  // EPS Parameters
  const [protein, setProtein] = useState(150);           // µg/g
  const [carbohydrate, setCarbohydrate] = useState(100); // µg/g
  const [biomass, setBiomass] = useState(1e8);           // copies/g
  
  // Abiotic Parameters
  const [sdAge, setSdAge] = useState(90);                // days
  const [grainSize, setGrainSize] = useState(0.3);       // mm (D50)
  const [inoculated, setInoculated] = useState(true);    // boolean
  
  // Scale and Model Selection
  const [selectedScale, setSelectedScale] = useState('pilot');
  const [selectedModel, setSelectedModel] = useState('A');
  
  // UI State
  const [activePreset, setActivePreset] = useState(null);
  const [showAllModels, setShowAllModels] = useState(true);

  // ============== MODEL COEFFICIENTS ==============
  // From thesis Tables 4.4, 5.4, 5.5, 5.6, 6.3
  
  const models = {
    // Chapter 4: Mini-scale (75 days operation)
    mini_ch4: {
      name: 'Mini-scale (Ch. 4)',
      description: '75-day mini SSF — protein/carbohydrate ratio as key predictor',
      models: {
        A: {
          name: 'Model A (Biochemical)',
          equation: 'λ = a₀ + a₁ × (protein/carbohydrate)',
          coefficients: { a0: -0.17, a1: 2.14 },
          rSquared: 0.81,
          pValue: 5.62e-4,
          calculate: (params) => {
            const ratio = params.protein / Math.max(params.carbohydrate, 0.1);
            return -0.17 + 2.14 * ratio;
          }
        },
        B: {
          name: 'Model B (Abiotic)',
          equation: 'λ = a₀ + a₁ × grain_size + a₂ × SD_inoc',
          coefficients: { a0: 0.86, a1: -1.44, a2: 0.64 },
          rSquared: 0.55,
          pValue: 0.039,
          calculate: (params) => {
            const inoc = params.inoculated ? 1 : 0;
            return 0.86 + (-1.44) * params.grainSize + 0.64 * inoc;
          }
        },
        C: {
          name: 'Model C (Combined)',
          equation: 'λ = a₀ + a₁ × (protein/carbohydrate) + a₂ × SD_inoc',
          coefficients: { a0: -0.22, a1: 1.90, a2: 0.34 },
          rSquared: 0.89,
          pValue: 5.97e-4,
          calculate: (params) => {
            const ratio = params.protein / Math.max(params.carbohydrate, 0.1);
            const inoc = params.inoculated ? 1 : 0;
            return -0.22 + 1.90 * ratio + 0.34 * inoc;
          }
        }
      }
    },
    
    // Chapter 5: Mini-scale (4 months)
    mini_ch5: {
      name: 'Mini-scale (Ch. 5)',
      description: '4-month mini SSF — protein as sole significant predictor',
      models: {
        A: {
          name: 'Model A & C (Protein)',
          equation: 'λ = a₀ + a₁ × protein',
          coefficients: { a0: -0.21, a1: 5.71e-3 },
          rSquared: 0.37,
          pValue: 0.0477,
          calculate: (params) => {
            return -0.21 + 5.71e-3 * params.protein;
          }
        }
      }
    },
    
    // Chapter 5: Midi-scale (1 year)
    midi: {
      name: 'Midi-scale (Ch. 5)',
      description: '1-year midi SSF — biomass and age as predictors',
      models: {
        A: {
          name: 'Model A (Biomass)',
          equation: 'λ = a₀ + a₁ × biomass',
          coefficients: { a0: 0.128, a1: 1.85e-9 },
          rSquared: 0.35,
          pValue: 0.0113,
          calculate: (params) => {
            return 0.128 + 1.85e-9 * params.biomass;
          }
        },
        B: {
          name: 'Model B & C (Age)',
          equation: 'λ = a₀ + a₁ × SD_age',
          coefficients: { a0: -0.0106, a1: 1.36e-3 },
          rSquared: 0.71,
          pValue: 4.8e-5,
          calculate: (params) => {
            return -0.0106 + 1.36e-3 * params.sdAge;
          }
        }
      }
    },
    
    // Chapter 5: Combined scales
    combined: {
      name: 'Combined Scales (Ch. 5)',
      description: 'Mini + Midi combined — biomass and age as predictors',
      models: {
        A: {
          name: 'Model A (Biomass)',
          equation: 'λ = a₀ + a₁ × biomass',
          coefficients: { a0: 0.0884, a1: 2.10e-9 },
          rSquared: 0.43,
          pValue: 3.30e-4,
          calculate: (params) => {
            return 0.0884 + 2.10e-9 * params.biomass;
          }
        },
        B: {
          name: 'Model B (Age)',
          equation: 'λ = a₀ + a₁ × SD_age',
          coefficients: { a0: -0.017, a1: 1.37e-3 },
          rSquared: 0.70,
          pValue: 1.9e-7,
          calculate: (params) => {
            return -0.017 + 1.37e-3 * params.sdAge;
          }
        }
      }
    },
    
    // Chapter 6: Pilot-scale (top 10 cm layer)
    pilot: {
      name: 'Pilot-scale (Ch. 6)',
      description: 'Pilot SSF top 10 cm — EPS composition explains 95-99% of variance',
      models: {
        A: {
          name: 'Model A (EPS Components)',
          equation: 'λ = a₀ + a₁ × carbohydrate + a₂ × protein',
          coefficients: { a0: -2.1110, a1: 4.16e-3, a2: 0.0133 },
          rSquared: 0.95,
          pValue: 0.0059,
          calculate: (params) => {
            return -2.1110 + 4.16e-3 * params.carbohydrate + 0.0133 * params.protein;
          }
        },
        B: {
          name: 'Model B (Age Only)',
          equation: 'λ = a₀ + a₁ × SD_age',
          coefficients: { a0: -0.3748, a1: 0.0029 },
          rSquared: 0.83,
          pValue: 0.0074,
          calculate: (params) => {
            return -0.3748 + 0.0029 * params.sdAge;
          }
        },
        C: {
          name: 'Model C (Full)',
          equation: 'λ = a₀ + a₁ × carb + a₂ × prot + a₃ × inoc',
          coefficients: { a0: -2.2556, a1: 4.78e-3, a2: 0.0124, a3: -0.1935 },
          rSquared: 0.99,
          pValue: 0.0041,
          calculate: (params) => {
            const inoc = params.inoculated ? 1 : 0;
            return -2.2556 + 4.78e-3 * params.carbohydrate + 0.0124 * params.protein + (-0.1935) * inoc;
          }
        }
      }
    }
  };

  // ============== PRESETS ==============
  const presets = {
    youngFilter: {
      name: 'Young Filter (1 month)',
      params: { protein: 50, carbohydrate: 80, biomass: 5e7, sdAge: 30, grainSize: 0.3, inoculated: false },
      description: 'Early ripening phase — low EPS, minimal removal',
      color: '#22c55e'
    },
    matureFilter: {
      name: 'Mature Filter (6 months)',
      params: { protein: 200, carbohydrate: 120, biomass: 2e8, sdAge: 180, grainSize: 0.3, inoculated: true },
      description: 'Well-developed Schmutzdecke — high protein/carb ratio',
      color: '#f59e0b'
    },
    fineSand: {
      name: 'Fine Sand Filter',
      params: { protein: 250, carbohydrate: 100, biomass: 3e8, sdAge: 90, grainSize: 0.15, inoculated: true },
      description: 'Fine sand with cohesive protein-rich EPS matrix',
      color: '#3b82f6'
    },
    coarseSand: {
      name: 'Coarse Sand Filter',
      params: { protein: 100, carbohydrate: 150, biomass: 1e8, sdAge: 90, grainSize: 0.5, inoculated: false },
      description: 'Coarse sand — lower EPS accumulation',
      color: '#8b5cf6'
    },
    highEPS: {
      name: 'High EPS Production',
      params: { protein: 350, carbohydrate: 180, biomass: 5e8, sdAge: 365, grainSize: 0.3, inoculated: true },
      description: 'Optimal Schmutzdecke — maximum removal potential',
      color: '#ef4444'
    }
  };

  const applyPreset = (presetKey) => {
    const p = presets[presetKey].params;
    setProtein(p.protein);
    setCarbohydrate(p.carbohydrate);
    setBiomass(p.biomass);
    setSdAge(p.sdAge);
    setGrainSize(p.grainSize);
    setInoculated(p.inoculated);
    setActivePreset(presetKey);
  };

  // ============== CALCULATIONS ==============
  
  // Current parameters object
  const currentParams = useMemo(() => ({
    protein,
    carbohydrate,
    biomass,
    sdAge,
    grainSize,
    inoculated
  }), [protein, carbohydrate, biomass, sdAge, grainSize, inoculated]);

  // Protein/Carbohydrate ratio
  const proteinCarbRatio = useMemo(() => {
    return protein / Math.max(carbohydrate, 0.1);
  }, [protein, carbohydrate]);

  // Calculate all model predictions
  const predictions = useMemo(() => {
    const results = {};
    Object.entries(models).forEach(([scaleKey, scaleData]) => {
      results[scaleKey] = {};
      Object.entries(scaleData.models).forEach(([modelKey, modelData]) => {
        const prediction = modelData.calculate(currentParams);
        results[scaleKey][modelKey] = {
          value: Math.max(0, prediction),
          model: modelData
        };
      });
    });
    return results;
  }, [currentParams]);

  // Current selected prediction
  const currentPrediction = useMemo(() => {
    const scaleModels = models[selectedScale]?.models;
    if (!scaleModels) return null;
    const modelData = scaleModels[selectedModel];
    if (!modelData) {
      // Fall back to first available model
      const firstModel = Object.keys(scaleModels)[0];
      return predictions[selectedScale]?.[firstModel];
    }
    return predictions[selectedScale]?.[selectedModel];
  }, [selectedScale, selectedModel, predictions]);

  // Model comparison data for bar chart
  const modelComparison = useMemo(() => {
    const data = [];
    Object.entries(models).forEach(([scaleKey, scaleData]) => {
      Object.entries(scaleData.models).forEach(([modelKey, modelData]) => {
        const pred = predictions[scaleKey]?.[modelKey];
        if (pred) {
          data.push({
            name: `${scaleData.name.split(' ')[0]} ${modelKey}`,
            fullName: `${scaleData.name} - ${modelData.name}`,
            value: pred.value,
            rSquared: modelData.rSquared,
            scale: scaleKey,
            model: modelKey,
            isSelected: scaleKey === selectedScale && modelKey === selectedModel
          });
        }
      });
    });
    return data;
  }, [predictions, selectedScale, selectedModel]);

  // Sensitivity analysis: Protein/Carb ratio effect
  const ratioSensitivity = useMemo(() => {
    const data = [];
    for (let ratio = 0.5; ratio <= 4; ratio += 0.25) {
      const testProtein = ratio * carbohydrate;
      const testParams = { ...currentParams, protein: testProtein };
      
      // Calculate for Chapter 4 Model A (uses ratio directly)
      const ch4ModelA = models.mini_ch4.models.A.calculate(testParams);
      // Calculate for Pilot Model A
      const pilotModelA = models.pilot.models.A.calculate(testParams);
      
      data.push({
        ratio: ratio,
        mini_ch4: Math.max(0, ch4ModelA),
        pilot: Math.max(0, pilotModelA),
        current: Math.abs(ratio - proteinCarbRatio) < 0.1
      });
    }
    return data;
  }, [currentParams, carbohydrate, proteinCarbRatio]);

  // Age sensitivity
  const ageSensitivity = useMemo(() => {
    const data = [];
    for (let age = 0; age <= 730; age += 30) {
      const testParams = { ...currentParams, sdAge: age };
      
      const midiB = models.midi.models.B.calculate(testParams);
      const pilotB = models.pilot.models.B.calculate(testParams);
      
      data.push({
        age,
        midi: Math.max(0, midiB),
        pilot: Math.max(0, pilotB),
        current: Math.abs(age - sdAge) < 15
      });
    }
    return data;
  }, [currentParams, sdAge]);

  // ============== UI COMPONENTS ==============
  
  const Slider = ({ label, value, setValue, min, max, step, unit, description, logScale = false }) => {
    const displayValue = logScale ? value.toExponential(1) : 
      (step < 0.01 ? value.toFixed(3) : step < 1 ? value.toFixed(2) : value.toFixed(0));
    
    return (
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-700 font-medium">{label}</span>
          <span className="font-mono text-blue-700 font-semibold">{displayValue} {unit}</span>
        </div>
        {description && <p className="text-xs text-gray-500 mb-2">{description}</p>}
        <input
          type="range"
          min={logScale ? Math.log10(min) : min}
          max={logScale ? Math.log10(max) : max}
          step={logScale ? 0.1 : step}
          value={logScale ? Math.log10(value) : value}
          onChange={(e) => {
            const val = parseFloat(e.target.value);
            setValue(logScale ? Math.pow(10, val) : val);
            setActivePreset(null);
          }}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
      </div>
    );
  };

  const MetricCard = ({ title, value, unit, subtitle, color = 'blue', highlight = false }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      amber: 'bg-amber-50 text-amber-600 border-amber-200',
      red: 'bg-red-50 text-red-600 border-red-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200'
    };
    return (
      <div className={`rounded-lg p-3 border-2 ${colorClasses[color]} ${highlight ? 'ring-2 ring-offset-2 ring-blue-400' : ''}`}>
        <div className="text-xs font-medium mb-1">{title}</div>
        <div className="text-xl font-bold">{value} <span className="text-sm font-normal">{unit}</span></div>
        {subtitle && <div className="text-xs mt-1 opacity-75">{subtitle}</div>}
      </div>
    );
  };

  // Get available models for selected scale
  const availableModels = models[selectedScale]?.models || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-5 mb-4 border border-gray-100">
          <h1 className="text-2xl font-bold text-gray-800">EPS-Based Removal Predictor</h1>
          <p className="text-sm text-gray-600 mt-1">
            Regression models linking EPS composition to SSF bacterial removal — Bai, Samari-Kermani et al. (2024, 2025)
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Protein/Carbohydrate ratio</span>
            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">Multi-scale validation</span>
            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">R² up to 0.99</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Panel: Parameters */}
          <div className="lg:col-span-1 space-y-4">
            {/* Scale & Model Selection */}
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Scale & Model Selection</h2>
              
              <div className="mb-3">
                <label className="text-xs text-gray-600 mb-1 block">Filter Scale</label>
                <select 
                  value={selectedScale}
                  onChange={(e) => {
                    setSelectedScale(e.target.value);
                    // Reset to first available model
                    const firstModel = Object.keys(models[e.target.value].models)[0];
                    setSelectedModel(firstModel);
                  }}
                  className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400"
                >
                  {Object.entries(models).map(([key, data]) => (
                    <option key={key} value={key}>{data.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1 italic">{models[selectedScale]?.description}</p>
              </div>
              
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Regression Model</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(availableModels).map(([key, data]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedModel(key)}
                      className={`p-2 rounded-lg text-xs font-medium border-2 transition-all ${
                        selectedModel === key
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      Model {key}
                      <div className="text-xs opacity-75">R²={data.rSquared}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Presets */}
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Scenario Presets</h2>
              <div className="grid grid-cols-1 gap-2">
                {Object.entries(presets).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => applyPreset(key)}
                    className={`text-left p-2 rounded-lg border transition-all ${
                      activePreset === key
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: preset.color }}></div>
                      <span className="text-xs font-semibold text-gray-700">{preset.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* EPS Parameters */}
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">EPS Composition</h2>
              
              <Slider 
                label="Protein content" 
                value={protein} 
                setValue={setProtein} 
                min={10} max={500} step={10} 
                unit="µg/g"
                description="EPS protein in Schmutzdecke"
              />
              
              <Slider
                label="Carbohydrate content"
                value={carbohydrate}
                setValue={setCarbohydrate}
                min={10} max={500} step={10}
                unit="µg/g"
                description="EPS carbohydrate in Schmutzdecke"
              />
              
              <Slider 
                label="Biomass" 
                value={biomass} 
                setValue={setBiomass} 
                min={1e6} max={1e10} step={1e6} 
                unit="copies/g"
                description="16S rRNA gene copies"
                logScale={true}
              />
              
              {/* Ratio display */}
              <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-700 font-medium">Protein/Carbohydrate Ratio</span>
                  <span className="text-lg font-bold text-green-800">{proteinCarbRatio.toFixed(2)}</span>
                </div>
                <p className="text-xs text-green-600 mt-1">
                  Higher ratio → higher "stickiness" → better removal
                </p>
              </div>
            </div>

            {/* Abiotic Parameters */}
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Abiotic Parameters</h2>
              
              <Slider
                label="Schmutzdecke age"
                value={sdAge}
                setValue={setSdAge}
                min={0} max={730} step={7}
                unit="days"
              />
              
              <Slider 
                label="Grain size (D₅₀)" 
                value={grainSize} 
                setValue={setGrainSize} 
                min={0.1} max={0.8} step={0.05} 
                unit="mm"
              />
              
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm text-gray-700">Schmutzdecke Inoculated</span>
                <button
                  onClick={() => {
                    setInoculated(!inoculated);
                    setActivePreset(null);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    inoculated
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {inoculated ? 'Yes' : 'No'}
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel: Results */}
          <div className="lg:col-span-2 space-y-4">
            {/* Key Metrics */}
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Predicted Removal</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard 
                  title="Log₁₀ Removal" 
                  value={currentPrediction?.value.toFixed(2) || '—'} 
                  unit="" 
                  subtitle={`${models[selectedScale]?.name} Model ${selectedModel}`}
                  color="green"
                  highlight={true}
                />
                <MetricCard 
                  title="R² (Model Fit)" 
                  value={currentPrediction?.model.rSquared.toFixed(2) || '—'} 
                  unit="" 
                  subtitle="Variance explained"
                  color="blue"
                />
                <MetricCard 
                  title="P/C Ratio" 
                  value={proteinCarbRatio.toFixed(2)} 
                  unit="" 
                  subtitle="Protein/Carbohydrate"
                  color="amber"
                />
                <MetricCard 
                  title="% Removal" 
                  value={((1 - Math.pow(10, -Math.max(0, currentPrediction?.value || 0))) * 100).toFixed(1)} 
                  unit="%" 
                  subtitle="Effluent reduction"
                  color="purple"
                />
              </div>
            </div>

            {/* Current Model Equation */}
            <div className="bg-gray-800 rounded-xl shadow-sm p-4 text-white">
              <h2 className="text-sm font-semibold mb-2">Current Model: {currentPrediction?.model.name}</h2>
              <div className="font-mono text-sm text-blue-300 mb-3">
                {currentPrediction?.model.equation}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {Object.entries(currentPrediction?.model.coefficients || {}).map(([key, val]) => (
                  <div key={key} className="bg-gray-700 rounded p-2 text-center">
                    <div className="text-gray-400">{key}</div>
                    <div className="font-bold text-green-300">{val.toExponential ? (Math.abs(val) < 0.01 ? val.toExponential(2) : val.toFixed(4)) : val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Model Comparison */}
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-sm font-semibold text-gray-700">Cross-Scale Model Comparison</h2>
                <button
                  onClick={() => setShowAllModels(!showAllModels)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  {showAllModels ? 'Show selected only' : 'Show all models'}
                </button>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart 
                  data={showAllModels ? modelComparison : modelComparison.filter(d => d.isSelected)}
                  margin={{ top: 10, right: 30, left: 10, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="name" 
                    fontSize={10}
                    angle={-30}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    label={{ value: 'Log₁₀ Removal', angle: -90, position: 'insideLeft', fontSize: 12 }}
                    fontSize={11}
                  />
                  <Tooltip 
                    formatter={(value, name, props) => [
                      `${value.toFixed(2)} log₁₀`,
                      props.payload.fullName
                    ]}
                    labelFormatter={() => ''}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {modelComparison.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.isSelected ? '#2563eb' : '#94a3b8'}
                        stroke={entry.isSelected ? '#1d4ed8' : 'none'}
                        strokeWidth={entry.isSelected ? 2 : 0}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Sensitivity: P/C Ratio */}
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Sensitivity: Protein/Carbohydrate Ratio Effect</h2>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={ratioSensitivity} margin={{ top: 10, right: 30, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="ratio" 
                    label={{ value: 'Protein/Carbohydrate Ratio', position: 'bottom', offset: 0, fontSize: 12 }}
                    fontSize={11}
                  />
                  <YAxis 
                    label={{ value: 'Log₁₀ Removal', angle: -90, position: 'insideLeft', fontSize: 12 }}
                    fontSize={11}
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${value.toFixed(2)}`, name === 'mini_ch4' ? 'Mini (Ch.4)' : 'Pilot (Ch.6)']}
                    labelFormatter={(label) => `P/C Ratio: ${label.toFixed(2)}`}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <ReferenceLine x={proteinCarbRatio} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Current', position: 'top', fontSize: 10, fill: '#ef4444' }} />
                  <Line type="monotone" dataKey="mini_ch4" stroke="#22c55e" strokeWidth={2} dot={false} name="Mini-scale (Ch.4)" />
                  <Line type="monotone" dataKey="pilot" stroke="#3b82f6" strokeWidth={2} dot={false} name="Pilot-scale (Ch.6)" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Sensitivity: Age Effect */}
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Sensitivity: Schmutzdecke Age Effect (Model B)</h2>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={ageSensitivity} margin={{ top: 10, right: 30, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="age" 
                    label={{ value: 'Schmutzdecke Age (days)', position: 'bottom', offset: 0, fontSize: 12 }}
                    fontSize={11}
                  />
                  <YAxis 
                    label={{ value: 'Log₁₀ Removal', angle: -90, position: 'insideLeft', fontSize: 12 }}
                    fontSize={11}
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${value.toFixed(2)}`, name === 'midi' ? 'Midi (Ch.5)' : 'Pilot (Ch.6)']}
                    labelFormatter={(label) => `Age: ${label} days`}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <ReferenceLine x={sdAge} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Current', position: 'top', fontSize: 10, fill: '#ef4444' }} />
                  <Line type="monotone" dataKey="midi" stroke="#f59e0b" strokeWidth={2} dot={false} name="Midi-scale (Ch.5)" />
                  <Line type="monotone" dataKey="pilot" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Pilot-scale (Ch.6)" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Key Insight Box */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl shadow-sm p-4 border border-green-200">
              <h2 className="text-sm font-semibold text-gray-700 mb-2">💡 Key Thesis Insight</h2>
              <p className="text-sm text-gray-600">
                <strong>EPS composition outperforms age as a removal predictor.</strong> The protein/carbohydrate 
                ratio reflects functional biofilm maturity: higher ratios indicate greater "stickiness" and 
                enhanced bacterial attachment. While age-based models (R² = 0.71–0.83) provide practical proxies, 
                EPS-based models achieve R² = 0.95–0.99 at pilot scale, demonstrating that biochemical 
                indicators are mechanistically superior to chronological metrics.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 text-center text-xs text-gray-500">
          Based on: Bai, X., Samari-Kermani, M., et al. (2024, 2025). SSF removal regression models.
          <br />
          PhD Thesis Chapters 4–6: <em>From Pores to Pilot Filters</em> — Utrecht University
        </div>
      </div>
    </div>
  );
};

export default EPSRemovalPredictor;
